import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  AlertTriangle,
  Building2,
  TrendingUp,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { adminARAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';

function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

function AgingBar({ label, amount, total, color, agingParam }) {
  const percentage = total > 0 ? (amount / total) * 100 : 0;
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    'red-dark': 'bg-red-700',
  };

  return (
    <Link
      to={`/admin/invoices?aging=${agingParam}`}
      className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors cursor-pointer group"
    >
      <span className="w-20 text-sm text-neutral-600 dark:text-neutral-400 group-hover:text-neutral-900 dark:group-hover:text-white">{label}</span>
      <div className="flex-1 bg-neutral-100 dark:bg-neutral-700 rounded-full h-3">
        <div
          className={`${colorClasses[color]} h-3 rounded-full transition-all`}
          style={{ width: `${Math.max(percentage, 1)}%` }}
        />
      </div>
      <span className="w-28 text-sm font-medium text-neutral-900 dark:text-white text-right">
        {formatCurrency(amount)}
      </span>
      <span className="w-12 text-sm text-neutral-500 dark:text-neutral-400 text-right">
        {percentage.toFixed(0)}%
      </span>
    </Link>
  );
}

export default function AROverview() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'ar-stats'],
    queryFn: () => adminARAPI.getStats(),
  });

  const stats = data?.data?.arStats;

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-700 dark:text-red-300">Failed to load AR statistics</p>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total AR Balance */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Total AR Balance</p>
              <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                {formatCurrency(stats.totalBalance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            {stats.openInvoiceCount} open invoices
          </div>
        </div>

        {/* Past Due */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Past Due</p>
              <p className={`text-2xl font-semibold mt-1 ${stats.totalPastDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {formatCurrency(stats.totalPastDue)}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stats.totalPastDue > 0 ? 'bg-red-100 dark:bg-red-900/50' : 'bg-green-100 dark:bg-green-900/50'}`}>
              <AlertTriangle className={`w-6 h-6 ${stats.totalPastDue > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`} />
            </div>
          </div>
          <div className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            {stats.totalBalance > 0 ? ((stats.totalPastDue / stats.totalBalance) * 100).toFixed(0) : 0}% of total AR
          </div>
        </div>

        {/* Companies with AR */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Companies with AR</p>
              <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                {stats.companiesWithOpenAR}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            with open balances
          </div>
        </div>

        {/* Payments Last 30 Days */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Payments (30d)</p>
              <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">
                {formatCurrency(stats.recentPayments.last30Days)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
            {formatCurrency(stats.recentPayments.last7Days)} last 7 days
          </div>
        </div>
      </div>

      {/* Aging Report and Top Companies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aging Report */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-neutral-900 dark:text-white">Aging Report</h3>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 flex items-center gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="space-y-1">
            <AgingBar
              label="Current"
              amount={stats.aging.current}
              total={stats.totalBalance}
              color="green"
              agingParam="current"
            />
            <AgingBar
              label="1-30 Days"
              amount={stats.aging.days1to30}
              total={stats.totalBalance}
              color="yellow"
              agingParam="1-30"
            />
            <AgingBar
              label="31-60 Days"
              amount={stats.aging.days31to60}
              total={stats.totalBalance}
              color="orange"
              agingParam="31-60"
            />
            <AgingBar
              label="61-90 Days"
              amount={stats.aging.days61to90}
              total={stats.totalBalance}
              color="red"
              agingParam="61-90"
            />
            <AgingBar
              label="90+ Days"
              amount={stats.aging.days90plus}
              total={stats.totalBalance}
              color="red-dark"
              agingParam="90plus"
            />
          </div>
        </div>

        {/* Top Companies */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-neutral-900 dark:text-white">Top Companies by Balance</h3>
            <Link
              to="/admin/invoices?status=unpaid"
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {stats.topCompanies.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 text-sm py-4 text-center">No companies with open balances</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 text-neutral-500 dark:text-neutral-400 font-medium">Company</th>
                    <th className="text-center py-2 text-neutral-500 dark:text-neutral-400 font-medium">Inv</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Balance</th>
                    <th className="text-right py-2 text-neutral-500 dark:text-neutral-400 font-medium">Oldest</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topCompanies.map((company) => (
                    <tr key={company.companyId} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                      <td className="py-2">
                        <Link
                          to={`/admin/companies/${company.companyId}`}
                          className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 truncate block max-w-[150px]"
                          title={company.companyName}
                        >
                          {company.companyName}
                        </Link>
                      </td>
                      <td className="py-2 text-center text-neutral-600 dark:text-neutral-300">
                        {company.invoiceCount}
                      </td>
                      <td className="py-2 text-right font-medium text-neutral-900 dark:text-white">
                        {formatCurrency(company.balance)}
                      </td>
                      <td className="py-2 text-right">
                        <span className={company.oldestDueDays > 30 ? 'text-red-600 dark:text-red-400' : 'text-neutral-600 dark:text-neutral-300'}>
                          {company.oldestDueDays > 0 ? `${company.oldestDueDays}d` : 'Current'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
